import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Power, LayoutDashboard } from "lucide-react";
import api from "@/lib/api";
import { useSettings, useSettingsCtx } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import MaintenancePage from "@/components/MaintenancePage";

const PREVIEW_KEY = "mg_preview_ok";

export default function MaintenanceGate({ children }) {
  const settings = useSettings();
  const { loading, patch } = useSettingsCtx();
  const { user, checking } = useAuth();
  const [searchParams] = useSearchParams();
  const [previewOk, setPreviewOk] = useState(() => localStorage.getItem(PREVIEW_KEY) === "1");
  const [busy, setBusy] = useState(false);

  // Validate the secret preview code coming from ?onizleme=CODE
  useEffect(() => {
    const code = searchParams.get("onizleme");
    if (!code) return;
    api
      .get("/maintenance/preview", { params: { code } })
      .then(({ data }) => {
        if (data?.ok) {
          localStorage.setItem(PREVIEW_KEY, "1");
          setPreviewOk(true);
        }
      })
      .catch(() => {});
  }, [searchParams]);

  const maintenance = !!settings?.maintenance_mode;
  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("mg_token");
  const isAdmin = !!user;

  // Site is live → no gating at all.
  if (!maintenance) return children;

  // Maintenance is ON. Wait until settings load and (if a token exists) auth resolves,
  // so we never flash the maintenance screen to a logged-in admin.
  if (loading || (checking && hasToken)) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#070707] text-neutral-500">Yükleniyor…</div>
    );
  }

  // Regular visitor → maintenance screen.
  if (!isAdmin && !previewOk) return <MaintenancePage settings={settings} />;

  async function turnOff() {
    setBusy(true);
    try {
      await api.post("/admin/maintenance", { enabled: false });
      patch({ maintenance_mode: false });
    } catch (e) {
      /* noop */
    } finally {
      setBusy(false);
    }
  }

  // Admin / preview visitor → real site with a thin status banner.
  return (
    <>
      <div
        data-testid="maintenance-admin-banner"
        className="sticky top-0 z-[60] flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 bg-gold px-4 py-2 text-center text-[13px] font-medium text-black"
      >
        <span>
          🔧 Bakım modu açık — bu görünümü yalnızca {isAdmin ? "yönetici" : "önizleme"} olarak görüyorsunuz.
        </span>
        {isAdmin && (
          <span className="flex items-center gap-2">
            <button
              onClick={turnOff}
              disabled={busy}
              data-testid="banner-turn-off"
              className="inline-flex items-center gap-1.5 rounded-md bg-black px-3 py-1 text-xs font-bold uppercase tracking-[0.05em] text-gold transition-opacity hover:opacity-85 disabled:opacity-60"
            >
              <Power className="h-3.5 w-3.5" /> Bakım modunu kapat
            </button>
            <Link
              to="/admin"
              data-testid="banner-go-admin"
              className="inline-flex items-center gap-1.5 rounded-md border border-black/40 px-3 py-1 text-xs font-bold uppercase tracking-[0.05em] text-black transition-colors hover:bg-black/10"
            >
              <LayoutDashboard className="h-3.5 w-3.5" /> Admin panele dön
            </Link>
          </span>
        )}
      </div>
      {children}
    </>
  );
}
